import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PageShell from '../components/ui/PageShell'
import PageHeader from '../components/ui/PageHeader'
import SectionCard from '../components/ui/SectionCard'
import DataTable from '../components/ui/DataTable'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import EmptyState from '../components/ui/EmptyState'
import LoadingState from '../components/ui/LoadingState'
import ErrorState from '../components/ui/ErrorState'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import useNotificationStore from '../services/notificationStore'
import { formatVersionWithCode } from '../utils/versionLabel'
import {
  createMobileAppRelease,
  deleteMobileAppRelease,
  listMobileAppReleases,
  publishMobileAppRelease,
  revokeMobileAppRelease,
} from '../services/mobileAppReleaseService'
import { computeFileSha256, isValidSha256 } from '../utils/sha256'
import { formatDateTime } from '../utils/date'

const initialForm = {
  platform: 'android',
  channel: 'debug',
  packageName: '',
  versionName: '',
  versionCode: '',
  minSupportedVersionCode: '',
  apkUrl: '',
  sha256: '',
  sizeBytes: '',
  shortDescription: '',
  releaseNotes: '',
  mandatory: false,
  rolloutPercent: 100,
}

function shortHash(value) {
  const hash = String(value || '')
  if (!hash) return '-'
  return `${hash.slice(0, 8)}...${hash.slice(-6)}`
}

function formatSize(value) {
  const size = Number(value || 0)
  if (!Number.isFinite(size) || size <= 0) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(2)} MB`
}

function validateForm(form) {
  const errors = {}
  if (form.platform !== 'android') errors.platform = 'Only android platform is supported'
  if (!form.channel || form.channel === 'production') errors.channel = 'Production channel is not allowed'
  if (!form.packageName.trim()) errors.packageName = 'Package name is required'
  if (!form.versionName.trim()) errors.versionName = 'Version name is required'
  const versionCode = Number(form.versionCode)
  if (!Number.isInteger(versionCode) || versionCode <= 0) errors.versionCode = 'Version code must be a positive integer'
  const minSupported = form.minSupportedVersionCode === '' ? null : Number(form.minSupportedVersionCode)
  if (minSupported != null && (!Number.isInteger(minSupported) || minSupported <= 0)) {
    errors.minSupportedVersionCode = 'Min supported version must be a positive integer'
  }
  if (minSupported != null && Number.isInteger(versionCode) && minSupported > versionCode) {
    errors.minSupportedVersionCode = 'Min supported version must not exceed version code'
  }
  if (!form.apkUrl.trim()) errors.apkUrl = 'APK URL is required'
  if (!isValidSha256(form.sha256)) errors.sha256 = 'SHA-256 must be 64 hex characters'
  const rolloutPercent = Number(form.rolloutPercent)
  if (!Number.isInteger(rolloutPercent) || rolloutPercent < 0 || rolloutPercent > 100) {
    errors.rolloutPercent = 'Rollout must be 0-100'
  }
  return errors
}

export default function MobileAppReleases() {
  const navigate = useNavigate()
  const showToast = useNotificationStore((state) => state.showToast)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const [filters, setFilters] = useState({ platform: 'android', channel: '', status: '' })
  const [form, setForm] = useState(initialForm)
  const [formErrors, setFormErrors] = useState({})
  const [createOpen, setCreateOpen] = useState(false)
  const [pendingPublish, setPendingPublish] = useState(null)
  const [pendingRevoke, setPendingRevoke] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [mandatoryConfirmText, setMandatoryConfirmText] = useState('')
  const [pendingRiskyCreateConfirm, setPendingRiskyCreateConfirm] = useState(false)
  const [riskyCreateConfirmText, setRiskyCreateConfirmText] = useState('')

  const formatReleaseVersion = (release) =>
    formatVersionWithCode(release?.versionName, release?.versionCode)

  const load = async () => {
    try {
      setError('')
      setLoading(true)
      const data = await listMobileAppReleases({ ...filters, pageSize: 100 })
      setRows(data.items)
    } catch (err) {
      setError(err?.message || 'Unable to load mobile app releases')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const summary = useMemo(() => {
    const published = rows.filter((row) => row.status === 'published').length
    const draft = rows.filter((row) => row.status === 'draft').length
    const revoked = rows.filter((row) => row.status === 'revoked').length
    const mandatory = rows.filter((row) => row.status === 'published' && row.mandatory).length
    return { published, draft, revoked, mandatory }
  }, [rows])

  const handleComputeSha = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const result = await computeFileSha256(file)
      setForm((prev) => ({ ...prev, sha256: result.sha256, sizeBytes: file.size }))
      if (result.warnings.length > 0) {
        showToast({ severity: 'warning', title: 'File check', message: result.warnings.join('. ') })
      }
    } catch (err) {
      showToast({ severity: 'error', title: 'Hash error', message: err?.message || 'Unable to calculate hash' })
    }
  }

  const buildCreatePayload = () => ({
    ...form,
    versionCode: Number(form.versionCode),
    minSupportedVersionCode: form.minSupportedVersionCode === '' ? null : Number(form.minSupportedVersionCode),
    sizeBytes: form.sizeBytes === '' ? null : Number(form.sizeBytes),
    shortDescription: form.shortDescription.trim() || null,
    longDescription: form.releaseNotes.trim() || null,
    releaseNotes: form.releaseNotes.trim() || null,
    rolloutPercent: Number(form.rolloutPercent),
  })

  const createDraft = async () => {
    const errors = validateForm(form)
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    try {
      await createMobileAppRelease(buildCreatePayload())
      showToast({ severity: 'success', title: 'Release created', message: 'Draft release metadata saved.' })
      setCreateOpen(false)
      setForm(initialForm)
      setFormErrors({})
      setPendingRiskyCreateConfirm(false)
      setRiskyCreateConfirmText('')
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Create failed', message: err?.message || 'Unable to create release' })
    }
  }

  const handleCreate = async () => {
    const errors = validateForm(form)
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    const rolloutPercent = Number(form.rolloutPercent)
    if (form.mandatory || rolloutPercent === 100) {
      setPendingRiskyCreateConfirm(true)
      return
    }

    await createDraft()
  }

  const handleConfirmRiskyCreate = async () => {
    if (riskyCreateConfirmText.trim() !== 'CONFIRM RISKY CREATE') return
    await createDraft()
  }

  const handlePublish = async () => {
    if (!pendingPublish) return
    if (pendingPublish.mandatory && mandatoryConfirmText !== 'PUBLISH MANDATORY') return

    try {
      await publishMobileAppRelease(pendingPublish.id)
      showToast({
        severity: 'success',
        title: 'Release published',
        message: `Published ${formatReleaseVersion(pendingPublish)}`,
      })
      setPendingPublish(null)
      setMandatoryConfirmText('')
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Publish failed', message: err?.message || 'Unable to publish release' })
    }
  }

  const handleRevoke = async () => {
    if (!pendingRevoke || !revokeReason.trim()) return
    try {
      await revokeMobileAppRelease(pendingRevoke.id, revokeReason.trim())
      showToast({
        severity: 'success',
        title: 'Release revoked',
        message: `Revoked ${formatReleaseVersion(pendingRevoke)}`,
      })
      setPendingRevoke(null)
      setRevokeReason('')
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Revoke failed', message: err?.message || 'Unable to revoke release' })
    }
  }

  const handleDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteMobileAppRelease(pendingDelete.id)
      showToast({
        severity: 'success',
        title: 'Release deleted',
        message: `Deleted ${formatReleaseVersion(pendingDelete)}`,
      })
      setPendingDelete(null)
      await load()
    } catch (err) {
      showToast({ severity: 'error', title: 'Delete failed', message: err?.message || 'Unable to delete release' })
    }
  }

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Debug/Internal APK Releases"
        subtitle="Manage Android APK releases for internal testing channels. Production Play Store updates must not be managed here."
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => load()}>Refresh</Button>
            <Button onClick={() => setCreateOpen((prev) => !prev)}>{createOpen ? 'Close form' : 'Create release'}</Button>
          </div>
        )}
      />

      <SectionCard title="Filters">
        <div className="grid gap-3 md:grid-cols-4">
          <FormField label="Platform"><Input value={filters.platform} readOnly /></FormField>
          <FormField label="Channel"><Input value={filters.channel} onChange={(event) => setFilters((prev) => ({ ...prev, channel: event.target.value }))} placeholder="debug/internal/alpha" /></FormField>
          <FormField label="Status"><Input value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} placeholder="draft/published/revoked" /></FormField>
          <div className="flex items-end"><Button onClick={() => load()}>Apply</Button></div>
        </div>
      </SectionCard>

      {createOpen ? (
        <SectionCard title="Create Draft Release Metadata" subtitle="Upload endpoint may be unavailable. Use APK URL + SHA-256 metadata mode.">
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Platform" error={formErrors.platform}><Input value={form.platform} onChange={(event) => setForm((prev) => ({ ...prev, platform: event.target.value }))} /></FormField>
            <FormField label="Channel" error={formErrors.channel}><Input data-testid="create-release-channel" value={form.channel} onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))} /></FormField>
            <FormField label="Package name" error={formErrors.packageName}><Input data-testid="create-release-package-name" value={form.packageName} onChange={(event) => setForm((prev) => ({ ...prev, packageName: event.target.value }))} /></FormField>
            <FormField label="Version name" error={formErrors.versionName}><Input data-testid="create-release-version-name" value={form.versionName} onChange={(event) => setForm((prev) => ({ ...prev, versionName: event.target.value }))} /></FormField>
            <FormField label="Version code" error={formErrors.versionCode}><Input data-testid="create-release-version-code" value={form.versionCode} onChange={(event) => setForm((prev) => ({ ...prev, versionCode: event.target.value }))} /></FormField>
            <FormField label="Min supported version code" error={formErrors.minSupportedVersionCode}><Input value={form.minSupportedVersionCode} onChange={(event) => setForm((prev) => ({ ...prev, minSupportedVersionCode: event.target.value }))} /></FormField>
            <FormField label="APK URL" error={formErrors.apkUrl}><Input data-testid="create-release-apk-url" value={form.apkUrl} onChange={(event) => setForm((prev) => ({ ...prev, apkUrl: event.target.value }))} /></FormField>
            <FormField label="SHA-256" error={formErrors.sha256}><Input data-testid="create-release-sha256" value={form.sha256} onChange={(event) => setForm((prev) => ({ ...prev, sha256: event.target.value }))} /></FormField>
            <FormField label="Rollout percent" error={formErrors.rolloutPercent}><Input data-testid="create-release-rollout" value={form.rolloutPercent} onChange={(event) => setForm((prev) => ({ ...prev, rolloutPercent: event.target.value }))} /></FormField>
            <FormField label="Size bytes"><Input value={form.sizeBytes} onChange={(event) => setForm((prev) => ({ ...prev, sizeBytes: event.target.value }))} /></FormField>
            <FormField label="Calculate SHA-256 from local file" helpText="File content is not uploaded. This only computes hash in-browser."><Input type="file" accept=".apk" onChange={handleComputeSha} /></FormField>
            <FormField label="Mandatory update"><input data-testid="create-release-mandatory" type="checkbox" checked={form.mandatory} onChange={(event) => setForm((prev) => ({ ...prev, mandatory: event.target.checked }))} /></FormField>
          </div>
          <div className="mt-3 grid gap-3">
            <FormField
              label="Short description"
              helpText="Shown first in the update banner as a concise summary."
            >
              <Input
                as="textarea"
                rows={2}
                data-testid="create-release-short-description"
                value={form.shortDescription}
                onChange={(event) => setForm((prev) => ({ ...prev, shortDescription: event.target.value }))}
              />
            </FormField>
            <FormField
              label="Long description / release notes"
              helpText="Use this for the full change log, including bug fixes and new features."
            >
              <Input
                as="textarea"
                rows={5}
                data-testid="create-release-long-description"
                value={form.releaseNotes}
                onChange={(event) => setForm((prev) => ({ ...prev, releaseNotes: event.target.value }))}
              />
            </FormField>
          </div>
          <div className="mt-4 flex justify-end"><Button onClick={handleCreate}>Save draft</Button></div>
        </SectionCard>
      ) : null}

      <SectionCard title="Release Summary">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded border border-outline bg-surface-elevated p-3">Published: {summary.published}</div>
          <div className="rounded border border-outline bg-surface-elevated p-3">Draft: {summary.draft}</div>
          <div className="rounded border border-outline bg-surface-elevated p-3">Revoked: {summary.revoked}</div>
          <div className="rounded border border-outline bg-surface-elevated p-3">Mandatory active: {summary.mandatory}</div>
        </div>
      </SectionCard>

      <SectionCard title="Releases">
        {loading ? <LoadingState text="Loading releases..." /> : null}
        {!loading && error ? <ErrorState text={error} action={<Button onClick={load}>Retry</Button>} /> : null}
        {!loading && !error && rows.length === 0 ? (
          <EmptyState
            title="No releases yet"
            description="Create the first debug/internal APK release to start testing server-driven app updates."
          />
        ) : null}
        {!loading && !error && rows.length > 0 ? (
          <DataTable
            columns={[
              { key: 'version', label: 'Version', sortable: true, sortAccessor: (row) => Number(row.versionCode || 0) },
              { key: 'channel', label: 'Channel', sortable: true },
              { key: 'status', label: 'Status', sortable: true },
              { key: 'qualification', label: 'Qualification', sortable: true, sortAccessor: (row) => row.qualificationEvidence?.status || 'missing' },
              { key: 'mandatory', label: 'Mandatory', sortable: true, sortAccessor: (row) => (row.mandatory ? 1 : 0) },
              { key: 'rolloutPercent', label: 'Rollout %', sortable: true, sortAccessor: (row) => Number(row.rolloutPercent || 0) },
              { key: 'sizeBytes', label: 'Size', sortable: true, sortAccessor: (row) => Number(row.sizeBytes || 0) },
              { key: 'sha256', label: 'SHA-256', sortable: true },
              { key: 'publishedAt', label: 'Published at', sortable: true, sortAccessor: (row) => row.publishedAt || '' },
              { key: 'actions', label: 'Actions' },
            ]}
            rows={rows}
            renderRow={(row) => (
              <tr key={row.id}>
                <td className="px-4 py-2 align-top">{formatReleaseVersion(row)}</td>
                <td className="px-4 py-2 align-top">{row.channel}</td>
                <td className="px-4 py-2 align-top">{row.status}</td>
                <td className="px-4 py-2 align-top">{row.qualificationEvidence?.invalidatedAt ? 'invalidated' : (row.qualificationEvidence?.status || 'missing')}</td>
                <td className="px-4 py-2 align-top">{row.mandatory ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 align-top">{row.rolloutPercent}</td>
                <td className="px-4 py-2 align-top">{formatSize(row.sizeBytes)}</td>
                <td className="px-4 py-2 align-top">{shortHash(row.sha256)}</td>
                <td className="px-4 py-2 align-top">{formatDateTime(row.publishedAt)}</td>
                <td className="px-4 py-2 align-top">
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/mobile/releases/${row.id}`)}>View</Button>
                    {row.status === 'draft' ? <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/mobile/releases/${row.id}?edit=1`)}>Edit</Button> : null}
                    {row.status === 'draft' ? <Button size="sm" aria-label={`Publish release ${row.id}`} onClick={() => setPendingPublish(row)}>Publish</Button> : null}
                    {row.status === 'published' ? <Button size="sm" variant="danger" aria-label={`Revoke release ${row.id}`} onClick={() => setPendingRevoke(row)}>Revoke</Button> : null}
                    <Button size="sm" variant="danger" aria-label={`Delete release ${row.id}`} onClick={() => setPendingDelete(row)}>Delete</Button>
                  </div>
                </td>
              </tr>
            )}
          />
        ) : null}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingPublish)}
        title="Publish release"
        message={pendingPublish ? `Publish ${formatReleaseVersion(pendingPublish)} on ${pendingPublish.channel}?` : ''}
        confirmText="Publish"
        onConfirm={handlePublish}
        onCancel={() => {
          setPendingPublish(null)
          setMandatoryConfirmText('')
        }}
      >
        {pendingPublish?.mandatory ? (
          <FormField label="Type PUBLISH MANDATORY to continue">
            <Input data-testid="publish-mandatory-input" value={mandatoryConfirmText} onChange={(event) => setMandatoryConfirmText(event.target.value)} />
          </FormField>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(pendingRevoke)}
        title="Revoke release"
        message={pendingRevoke ? `Revoke ${formatReleaseVersion(pendingRevoke)}?` : ''}
        confirmText="Revoke"
        confirmVariant="danger"
        onConfirm={handleRevoke}
        onCancel={() => {
          setPendingRevoke(null)
          setRevokeReason('')
        }}
      >
        <FormField label="Reason is required">
          <Input data-testid="revoke-reason-input" value={revokeReason} onChange={(event) => setRevokeReason(event.target.value)} />
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete release"
        message={pendingDelete ? `Delete ${formatReleaseVersion(pendingDelete)} permanently?` : ''}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => {
          setPendingDelete(null)
        }}
      />

      <ConfirmDialog
        open={pendingRiskyCreateConfirm}
        title="Confirm risky draft create"
        message="This draft is mandatory or uses 100% rollout. Type CONFIRM RISKY CREATE to continue."
        confirmText="Create risky draft"
        onConfirm={handleConfirmRiskyCreate}
        onCancel={() => {
          setPendingRiskyCreateConfirm(false)
          setRiskyCreateConfirmText('')
        }}
      >
        <FormField label="Type CONFIRM RISKY CREATE to continue">
          <Input data-testid="risky-create-confirm-input" value={riskyCreateConfirmText} onChange={(event) => setRiskyCreateConfirmText(event.target.value)} />
        </FormField>
      </ConfirmDialog>
    </PageShell>
  )
}
